import * as swcHelpers from '@swc/helpers';
import { useRef, useEffect } from 'react';
import { useTealiumEventProperties, useDispatchTrackPageView, useDispatchTrackEvent } from '@grandvisionhq/svc-tealium/events';

/**
 * This hook handles Tealium event tracking for login events and page views.
 * It tracks events when the common user properties become available, ensuring
 * that Tealium events are dispatched only once. The tracking operations are
 * awaited and errors are logged but do not disrupt the application flow.
 *
 * @param {string | string[] | undefined} props.queryLoginMethod - The method used for logging in (e.g., 'social').
 * @param {'callback' | 'logged-out'} props.type - The type of callback, which defines whether to track the login event.
 *
 * @description
 * - Tracks login success events and page views for "my-account-login".
 * - Ensures events are only fired once using a `hasTrackedRef` to avoid duplicate dispatches.
 * - Tealium tracking functions are awaited to ensure proper execution, but any errors
 *   are caught and logged without impacting the user experience.
 */ const useAccountLoginTracking = ({ queryLoginMethod , type  })=>{
    const { commonEventProperties , getCommonUserProperties  } = useTealiumEventProperties();
    const hasTrackedRef = useRef(false);
    const dispatchTrackPageView = useDispatchTrackPageView();
    const dispatchTrackEvent = useDispatchTrackEvent();
    useEffect(()=>{
        const trackEvents = function() {
            var _ref = swcHelpers.asyncToGenerator(function*() {
                try {
                    if (hasTrackedRef.current) return;
                    const commonUserProperties = yield getCommonUserProperties();
                    if (!commonUserProperties) return;
                    const loginPageView = ()=>Promise.resolve(swcHelpers.objectSpread({
                            id: 'VirtualPage-View',
                            Page_Section1: 'Account',
                            Page_Type: 'Static',
                            Page_Design: 'Login'
                        }, commonEventProperties, commonUserProperties))
                    ;
                    const loginEvent = ()=>Promise.resolve(swcHelpers.objectSpread({
                            id: 'Event',
                            Events_UserLogin: '1'
                        }, commonUserProperties))
                    ;
                    if (queryLoginMethod === 'social' || type === 'callback') {
                        yield dispatchTrackEvent(loginEvent);
                        yield dispatchTrackPageView(loginPageView);
                    }
                    hasTrackedRef.current = true;
                } catch (error) {
                    console.error('Tealium tracking failed:', error);
                }
            });
            return function trackEvents() {
                return _ref.apply(this, arguments);
            };
        }();
        trackEvents();
    /*
      The exhaustive deps rule is disabled here for dispatchTrackEvent and dispatchTrackPageView
      because adding the Tealium related functions crashes the login flow. Likely related again
      to the React 18 upgrade. flushSync can be a solution, but ideally we avoid this.
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        commonEventProperties,
        queryLoginMethod,
        getCommonUserProperties,
        type
    ]);
};

export { useAccountLoginTracking };
//# sourceMappingURL=use-account-login-tracking.js.map
